import { yupResolver } from '@hookform/resolvers/yup';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  createUserProfile,
  useGetSpecialtiesList,
} from '../api/userprofile';
import { UserProfileForManipulation } from '../dto/userprofile';
import Typeahead from '@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';

const schema = yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  npi: yup
    .string()
    .test(
      'npi',
      'Npi must be valid',
      (npi) => !npi || npi?.length === 0 || (/^\d{10}$/.test(String(npi)) && npi?.length === 10)
    )
    .nullable(),
  phoneNumber: yup
    .string()
    //eslint-disable-next-line
    .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/, {
      message: 'Phone number must be valid',
      excludeEmptyString: true,
    })
    .required('Phone is required'),
  specialty: yup.string().required('Specialty is required'),
  employer: yup.string().nullable(),
  termsAndConditions: yup
    .boolean()
    .required('The privacy policy and terms of service must be accepted to continue')
    .oneOf([true], 'The privacy policy and terms of service must be accepted to continue'),
});

interface ProfileInformationFormProps {
  onCompleted?: () => void;
}

const ProfileInformationForm = ({ onCompleted }: ProfileInformationFormProps) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<UserProfileForManipulation>({
    resolver: yupResolver(schema),
  });

  const { data: specialtiesData } = useGetSpecialtiesList();
  const specialties = (specialtiesData || []).map((specialty) => ({
    value: specialty,
    label: specialty,
  }));

  const [isSaving, setIsSaving] = useState(false);

  const onSubmit = (data: UserProfileForManipulation) => {
    setIsSaving(true);

    createUserProfile(data)
      .then(() => {
        setIsSaving(false);
        onCompleted && onCompleted();
      })
      .catch(() => {
        setIsSaving(false);
      });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-7 gap-2 my-3">
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextInput
              id="firstName"
              type="text"
              htmlName="firstName"
              label="First Name"
              required={true}
              error={errors?.firstName?.message}
              value={field.value}
              onChange={field.onChange}
              wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextInput
              id="lastName"
              type="text"
              htmlName="lastName"
              label="Last Name"
              required={true}
              error={errors?.lastName?.message}
              value={field.value}
              onChange={field.onChange}
              wrapperProps={{ className: 'col-span-7 md:col-span-4' }}
            />
          )}
        />
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <TextInput
              id="phone"
              type="text"
              htmlName="phone"
              label="Phone"
              required={true}
              placeholder="123-123-1234"
              error={errors?.phoneNumber?.message}
              value={field.value}
              onChange={field.onChange}
              wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
            />
          )}
        />
        <div className="col-span-7 md:col-span-4">
          <Controller
            name="specialty"
            control={control}
            render={({ field }) => (
              <Listbox
                  label="Specialty"
                  data={specialties}
                  selected={specialties.find((item) => item.value === field.value)?.value || ''}
                  setSelected={(item) => {
                    field.onChange(item);
                  }}
                  required={true}
                  error={errors?.specialty?.message}
                />
            )}
          />
        </div>
        <div className="col-span-7 col-start-1 mt-2">
          <span className="italic">Optional</span>
        </div>
        <Controller
          name="employer"
          control={control}
          render={({ field }) => (
            <TextInput
              id="employer"
              type="text"
              htmlName="employer"
              label="Employer"
              value={field.value!}
              onChange={field.onChange}
              wrapperProps={{ className: 'col-span-7 md:col-span-4' }}
            />
          )}
        />
        <Controller
          name="npi"
          control={control}
          render={({ field }) => (
            <TextInput
              id="npi"
              type="text"
              htmlName="npi"
              label="NPI"
              error={errors?.npi?.message}
              value={field.value}
              onChange={field.onChange}
              wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
            />
          )}
        />
        <Controller
          name="termsAndConditions"
          control={control}
          render={({ field }) => (
            <div className="col-span-7">
              <Checkbox
                id="termsAndConditions"
                htmlName="termsAndConditions"
                label={
                  <>
                    I accept the{' '}
                    <a
                      href="/resources/oncolens_privacy_policy.pdf"
                      className="text-blue-500 underline"
                      target="_blank"
                    >
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a
                      href="/resources/oncolens_terms_of_service.pdf"
                      className="text-blue-500 underline"
                      target="_blank"
                    >
                      Terms of Service
                    </a>
                    .<span className="text-red-600">*</span>
                  </>
                }
                value={field.value!}
                onClick={field.onChange}
              />
              {errors?.termsAndConditions?.message ? (
                <p className="mt-2 text-sm text-red-600" data-cy={`termsAndConditions-error`}>{errors?.termsAndConditions?.message}</p>
              ) : null}
            </div>
          )}
        />
      </div>
      <ButtonWithLoading
        disabled={isSaving}
        loading={isSaving}
        type="submit"
        flexStyle="inline-flex"
        className="w-auto px-4"
      >
        Continue
      </ButtonWithLoading>
    </form>
  );
};

export { ProfileInformationForm as default };
